import { useContext, useEffect, useSyncExternalStore } from 'react';

import type { ConnectionState } from '@/shared/realtime';

import { type BoardPresence, EMPTY_PRESENCE, RealtimeContext } from './context';

/** Hints for one board while the component is mounted. */
export function useBoardRealtime(boardId: string | undefined): void {
  const api = useContext(RealtimeContext);
  const client = api?.client ?? null;
  useEffect(() => {
    if (!client || !boardId) return;
    return client.subscribeBoard(boardId);
  }, [client, boardId]);
}

/** Hints for one database (its contents, structure, shared views, access) while mounted. The
 * server accepts the subscription only for holders of `database.view`. */
export function useDatabaseRealtime(databaseId: string | undefined): void {
  const api = useContext(RealtimeContext);
  const client = api?.client ?? null;
  useEffect(() => {
    if (!client || !databaseId) return;
    return client.subscribeDatabase(databaseId);
  }, [client, databaseId]);
}

const NOOP_SUBSCRIBE = (): (() => void) => () => undefined;

export function usePresence(boardId: string | undefined): BoardPresence {
  const api = useContext(RealtimeContext);
  const store = api?.presence;
  return useSyncExternalStore(
    store?.subscribe ?? NOOP_SUBSCRIBE,
    () => (store && boardId ? store.get(boardId) : EMPTY_PRESENCE),
    () => EMPTY_PRESENCE,
  );
}

/** Tell others which card of the board this tab is editing (cleared on unmount). */
export function useReportEditing(boardId: string | undefined, cardId: string | null): void {
  const api = useContext(RealtimeContext);
  const client = api?.client ?? null;
  useEffect(() => {
    if (!client || !boardId || cardId === null) return;
    client.setEditing(boardId, cardId);
    return () => {
      client.setEditing(boardId, null);
    };
  }, [client, boardId, cardId]);
}

export function useConnectionState(): ConnectionState {
  const api = useContext(RealtimeContext);
  const client = api?.client ?? null;
  return useSyncExternalStore(
    (listener) => client?.onState(listener) ?? (() => undefined),
    () => client?.state ?? 'idle',
    () => 'idle',
  );
}
